import { FocusEvent, FocusEventHandler, InputHTMLAttributes, ReactNode, Ref } from 'react'

import { InputLabelProps, SelectChangeEvent } from '@mui/material'
import { GridProps } from '@mui/material/Grid'
import { InputProps } from '@mui/material/Input'
import format from 'date-fns/fp/format'
import isValid from 'date-fns/isValid'
import parse from 'date-fns/parse'
import { FormikContextType, FormikHelpers, FormikProps } from 'formik'
import { TOptionsBase } from 'i18next'
import * as Yup from 'yup'

// ---------------------------------------------------------------------------
// Enums (inlined from ui-library-core internal packages)
// ---------------------------------------------------------------------------

export enum DiseaseType {
  INFECTIOUS = 'INFECTIOUS',
  HEART = 'HEART',
  CIRCULATORY_VASCULAR = 'CIRCULATORY_VASCULAR',
  LUNG = 'LUNG',
  BONE_MUSCULAR_COGNITIVE_TISSUE = 'BONE_MUSCULAR_COGNITIVE_TISSUE',
  CANCER = 'CANCER',
  METABOLIC = 'METABOLIC',
  GASTRIC_INTESTINAL = 'GASTRIC_INTESTINAL',
  LIVER_BILIARY = 'LIVER_BILIARY',
  KIDNEY_URINARY_TRACT = 'KIDNEY_URINARY_TRACT',
  PSYCHOLOGICAL = 'PSYCHOLOGICAL',
  MEDICATION_ALLERGIES = 'MEDICATION_ALLERGIES',
  OTHER_ALLERGIES = 'OTHER_ALLERGIES',
  SURGERY = 'SURGERY',
  OTHER = 'OTHER',
}

export enum InsuranceType {
  PUBLIC = 'SOCIAL_INSURANCE',
  PRIVATE = 'PRIVATE_INSURANCE',
}

export enum Language {
  'ru' = 'ru',
  'ku' = 'ku',
  'tr' = 'tr',
  'en' = 'en',
  'fa' = 'fa',
  'ar' = 'ar',
  'hr' = 'hr',
  'bs' = 'bs',
  'sr' = 'sr',
  'de' = 'de',
  'el' = 'el',
  'hu' = 'hu',
  'ro' = 'ro',
  'pl' = 'pl',
  'es' = 'es',
  'it' = 'it',
  'fr' = 'fr',
  'pt' = 'pt',
  'no' = 'no',
  'vi' = 'vi',
  'sq' = 'sq',
  'ti' = 'ti',
  'hi' = 'hi',
  'bg' = 'bg',
  'cs' = 'cs',
  'tl' = 'tl',
  'lv' = 'lv',
  'se' = 'se',
  'kr' = 'kr',
  'fi' = 'fi',
  'da' = 'da',
  'nl' = 'nl',
  'zh' = 'zh',
  'ja' = 'ja',
  'af' = 'af',
  'mo' = 'mo',
  'sw' = 'sw',
}

export type ContentfulClientConfig = {
  space: string
  accessToken: string
}

// ---------------------------------------------------------------------------
// Date formats (inlined from ui-library-core @constants/date/date)
// ---------------------------------------------------------------------------

export const DATE_FORMATS = {
  ISO_SHORT: 'yyyy-MM-dd',
  DAY_MONTH_YEAR: 'dd.MM.yyyy',
  LOCAL_MONTH_YEAR: 'MMMM yyyy',
} as const

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WishAndExpectation = {
  key: string
  flag?: boolean
  score?: number | null
  requireTrue?: string
  requireFalse?: string
}

export type Disease = {
  type: DiseaseType
  name: string
  medications?: string[]
  custom?: boolean
}

export type Form = {
  code: string
  patientReferenceId: string
  pdfReference: string

  // Contact person
  filedForDifferentPerson?: boolean
  contactPersonFirstName?: string
  contactPersonLastName?: string
  contactPersonRelation?: string
  contactPersonBirthday?: string
  contactPersonStreet?: string
  contactPersonZipcode?: string
  contactPersonCity?: string

  // Personal Details
  firstName: string
  lastName: string
  gender: string
  birthday: string
  placeOfBirth: string // missing in API

  // Contact Details
  street: string
  zipCode: string
  city: string
  email: string
  phone: string
  alternativePhone: string

  // Miscellaneous
  occupation: string
  isPregnant: boolean
  pregnantMonths?: number
  cigaretteConsumption: string
  alcoholConsumption: string
  drugConsumption: string
  interestedInProfile: boolean
  bodyWeightInKg: number
  bodyHeightInCm: number

  // Insurance
  type: string // insurance type
  hasCareLevel: string | boolean
  careLevelLimitedInTime?: string | boolean
  entitledToIntegrationAssistance: string | boolean
  integrationAssistanceBasedOnType?: string | boolean
  insuranceCompany: string
  insuranceCompanyId?: string
  hasFamilyInsurance?: boolean
  livesOnDifferentAddress?: boolean
  holderFirstName: string
  holderLastName: string
  holderBirthday: string
  holderCity?: string
  holderStreet?: string
  holderZipcode?: string
  hasDentalInsurance?: boolean
  dentalInsuranceCompany?: string
  dentalInsuranceCompanyId?: string
  hasCivilServiceAllowance?: boolean

  // Pre conditions
  diseases: Disease[]
  wishesAndExpectations: WishAndExpectation[]
  medications: string[] // missing in API, these are medicines not related to infections
  allergyPass: boolean

  // ToS
  dataUsageChecked: boolean
  dataTruthyness?: boolean
  dataProtection?: boolean
  signatureEncoded: string
  confidentialityAgreementAccepted: boolean
  marketingCommunicationConsentAccepted?: boolean
  insuranceConsentAccepted?: boolean
  tosPatientAccountAccepted?: boolean

  referral: string

  // Miscellaneous
  visitReason?: string
  addressGoogleVerified: boolean
  contactAddressGoogleVerified: boolean
  language: string

  startAt: string

  /* Using double underscores to make it easy to understand that this field should not be sent to the API */
  bookedForDifferentPerson: boolean
  hasAllergies: 'YES' | 'NO' | 'UNKNOWN'
}

export type FormProps = {
  initialValues: Record<string, unknown | WishAndExpectation[]>
  onSubmit: (values: Record<string, unknown>) => void
  portalAttr?: string
  loading: boolean
  innerRef?: Ref<FormikProps<Record<string, unknown>>>
}

export type AllergiesAndMedicationsFormProps = FormProps & {
  initialValues: Record<string, unknown> & { medications?: string[] }
}

export interface WishesProps extends FormProps {
  wishes?: WishAndExpectation[]
}

export interface PreconditionsProps extends FormProps {
  // Diseases to be rendered
  diseases: Disease[]
  showSurgery?: boolean
  logic?: UsePreConditionsToggleReturn

  // This prop is responsible by provide the label of a disease group
  // by index. The label usually is "Do you have:" or "Do you had"
  diseaseGroupLabelByIndex?: Record<number, string>
}

export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}/

export const BDAY_REGEX = /\d{2}\.\d{2}\.\d{2,4}/

export const LONG_BDAY_FORMAT = DATE_FORMATS.DAY_MONTH_YEAR

export const toISODate = format(DATE_FORMATS.ISO_SHORT)

export const formatBirthday = (date: string) => {
  const valueTrimmed = date.trim()
  const asDate = new Date(date)

  if (!valueTrimmed) return valueTrimmed
  if (isValid(asDate) && ISO_DATE_REGEX.test(valueTrimmed)) return toISODate(asDate)
  if (!BDAY_REGEX.test(valueTrimmed)) throw new Error('Wrong birthday format')

  return toISODate(parse(valueTrimmed, LONG_BDAY_FORMAT, new Date()))
}

export type InsuranceCompany = {
  city: string
  companyName: string
  country: string
  createdAt: string
  email: string
  insurerName: string
  modifiedAt: string
  postCode: string
  referenceId: string
  street: string
  telephoneNumbers: Array<string>
}

export type InsuranceFormProps = FormProps & {
  insuranceCompanies: InsuranceCompany[]
  clinicEmail?: string
}

export type TranslationsConfig = { clientConfig: ContentfulClientConfig; children: ReactNode; language: Language }

export type TranslateProps = {
  className?: string
  options?: TOptionsBase
  component?: string
  dangerous?: boolean
  fallback?: string
} & Record<string, unknown>

export interface WishesFormProps extends FormProps {
  wishes?: WishAndExpectation[]
}

export type ConsentsFormProps = FormProps & {
  optins?: Optins
  clinicReferenceId?: string
  anaBackendUrl?: string
  offerInsurance?: boolean
  privacyPolicyUrl?: string
}

export type PersonalInformationFormProps = FormProps & { canBeFilledForDifferentPerson: boolean }

export type Optins = {
  dataProtectionAgreementAccepted: boolean
  patientInformationAccepted: boolean
  confidentialityAgreementAccepted: boolean
  tosPatientAccountAccepted: boolean
  marketingCommunicationConsentAccepted: boolean
}

export interface PreconditionsFormProps extends FormProps {
  logic?: UsePreConditionsToggleReturn
}

export type UsePreConditionsToggleReturn = {
  collapseStates: Record<string, boolean>
  selectedDiseases: Disease[]
  errors: Partial<Record<DiseaseType, string>>
  hasError: boolean
  nextError?: { type: DiseaseType; error: string }
  handleToggleChange: (event: React.ChangeEvent<{ name: string }>, checked: boolean) => void
  handleDiseaseCheck: (diseases: Disease[]) => void
  handleDiseaseChange: (_: unknown, diseases: Disease[]) => void
  handleSubmit: () => void
}

export const ensureConditionalFieldValues = (values: Partial<Form>): Partial<Form> => {
  const clone = { ...values }

  if (values.gender === 'MALE') clone.isPregnant = false

  if (!values.isPregnant) delete clone.pregnantMonths

  if (values.type !== InsuranceType.PRIVATE) {
    delete clone.insuranceCompanyId
    delete clone.insuranceCompany
  }

  if (values.type === InsuranceType.PRIVATE) {
    delete clone.dentalInsuranceCompany
    delete clone.dentalInsuranceCompanyId
    clone.hasDentalInsurance = false
  }

  if (!values.hasDentalInsurance) {
    delete clone.dentalInsuranceCompany
    delete clone.dentalInsuranceCompanyId
  }

  if (!values.hasFamilyInsurance) {
    delete clone.holderCity
    delete clone.holderStreet
    delete clone.holderZipcode
    delete clone.holderFirstName
    delete clone.holderLastName
    delete clone.holderBirthday
  }

  return clone
}

export const wishesForPage1: WishAndExpectation[] = [
  {
    key: 'bad_breath',
    flag: false,
  },
  {
    key: 'grind_teeth',
    flag: false,
  },
  {
    key: 'snor',
    flag: false,
  },
  {
    key: 'have_amalgam_filling_to_replace',
    flag: false,
  },
]

export const wishesForPage2: WishAndExpectation[] = [
  {
    key: 'has_toothache',
    score: 0,
  },
  {
    key: 'satisfied_tooth_color',
    score: 0,
  },
  {
    key: 'satisfied_tooth_position',
    score: 0,
  },
]

export const wishesForPage3: WishAndExpectation[] = [
  {
    key: 'what_is_important',
    score: 0,
  },
]

export const preparations: WishAndExpectation[] = [
  {
    key: 'difficult_nasal_breathing',
    flag: false,
  },
  {
    key: 'has_spinal_complaints',
    flag: false,
  },
  {
    key: 'has_bridges',
    flag: undefined,
  },
  {
    key: 'has_removable_dentures',
    flag: false,
  },
  {
    key: 'pronounced_gag_reflex',
    flag: false,
  },
  {
    key: 'afraid_of_dentist_treatment',
    flag: false,
  },
  {
    key: 'teeth_crowding',
    flag: false,
  },
]

export type InsuranceFieldProps = {
  label?: string
  helperText?: string
  value?: string
  formik?: FormikProps<Partial<Form>> & { handleFocus: (e: FocusEvent) => void }
  onChange?: (values: { referenceId?: string; value: string }) => void
  onBlur?: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  name: string
  inputValue?: string
  isRequired?: boolean | ((formValues: Partial<Form>) => boolean)
  error?: string
  companiesOptions: InsuranceCompany[]
}

export type WishesToggleFieldContainerProps = {
  label: string
  wishKey: string
}

export type WishesRatingFieldContainerProps = {
  wishKey: string
  label: string
  required?: boolean
}

export type DiseaseGroupFieldProps = {
  type: DiseaseType
  hideMedications?: boolean
  activeHelperText?: string
  customInputPlaceholder?: string
  label?: string

  error?: string

  // The diseases to be rendered inside the group
  diseases: Disease[]

  // The diseases that the user has selected
  selectedDiseases?: Disease[]

  // All the possible diseases
  allDiseases?: Disease[]

  checked?: boolean
  onToggle?: (event: React.ChangeEvent<{ name: string }>, checked: boolean) => void
  onDiseaseCheck?: (diseases: Disease[]) => void
  onDiseaseChange?: (disease: Disease, diseases: Disease[]) => void
}

export type Option = {
  label: string
  value: string
}

export type ToggleMultiCheckboxProps = {
  label: string
  options: Option[]
  // Toggle value
  value: boolean
  // Checkboxes values
  selectedValues?: string[]
  onSelectedValuesChange?: (newValues: string[]) => void
  onToggleChange?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  allowCustomOptions?: boolean
  helperText?: string
  error?: string
}

export type AllergiesFieldProps = {
  label: string
  diseaseType: DiseaseType.OTHER_ALLERGIES | DiseaseType.MEDICATION_ALLERGIES
  options: Disease[]
  error?: string
}

export type CheckboxFieldProps = {
  className?: string
  name: string
  label: ReactNode
  error?: string
  helperText?: boolean
}

export type TextInputProps = {
  value?: string
  className?: string
  error?: string
  name: string
  label: ReactNode
  required?: boolean
  helperText?: ReactNode
  placeholder?: string
  InputLabelProps?: InputLabelProps
  type?: InputHTMLAttributes<HTMLInputElement>['type']
  inputComponent?: InputProps['inputComponent']
  multiline?: boolean
  rows?: number
  endAdornment?: InputProps['endAdornment']
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: FocusEventHandler
  onFocus?: FocusEventHandler
}

export type SelectInputProps = {
  value?: string
  className?: string
  error?: string
  name: string
  label: ReactNode
  required?: boolean
  helperText?: ReactNode
  options: Option[]
  onChange?: (e: SelectChangeEvent<unknown>) => void
  onBlur?: (e: React.FocusEvent) => void
}

export type FormHelpersProviderReturn = {
  setFieldValue: FormikHelpers<Partial<Form>>['setFieldValue']
  setFieldTouched: FormikHelpers<Partial<Form>>['setFieldTouched']
  getFieldProps: FormikContextType<Partial<Form>>['getFieldProps']
  setValues: FormikContextType<Partial<Form>>['setValues']
  setErrors: FormikHelpers<Partial<Form>>['setErrors']
  setTouched: FormikContextType<Partial<Form>>['setTouched']
}

export type FieldSettings = {
  label: string
  name: string
  flagSource?: (country: string) => string
  options?: Option[]
  isRequired?: boolean | ((formValues: Partial<Form>) => boolean)
  justificativeTooltipText?: string
  helperText?: string
  helperData?: object
  optional?: boolean
  __type?: 'toggle' | 'text' | 'radioGroup' | 'address' | 'phone' | 'select' | 'date' | string
  inputProps?: {
    placeholder?: string
    type?: string
    withText?: boolean
    InputLabelProps?: InputLabelProps
    inputComponent?: InputProps['inputComponent']
    autoFocus?: boolean
    onFocus?: FocusEventHandler<HTMLInputElement>
    onBlur?: FocusEventHandler<HTMLInputElement>
    disabled?: boolean
  }
  validation?:
    | Yup.StringSchema<string | null | undefined>
    | Yup.DateSchema<Date | undefined>
    | Yup.BooleanSchema<boolean | null | undefined>
    | Yup.NumberSchema<number | null | undefined>
    | Yup.MixedSchema<object | null | undefined>
    | Yup.NotRequiredArraySchema<unknown>
  tabIndex?: number
  visibleWhen?: (formValues: Partial<Form>) => boolean
  hooks?: {
    beforeChange?: (value: unknown, helpers: FormHelpersProviderReturn, e?: React.ChangeEvent<HTMLInputElement>) => void
  }
}

type GridSizeProps = {
  xs?: boolean | 'auto' | number | string
  sm?: boolean | 'auto' | number | string
  md?: boolean | 'auto' | number | string
  lg?: boolean | 'auto' | number | string
  xl?: boolean | 'auto' | number | string
}

export type FieldLayoutItem = GridSizeProps & {
  type: 'field'
  key?: string
  field: FieldSettings
}

export type ElementLayoutItem = GridSizeProps & {
  type: 'custom'
  key: string
  render: React.FC<{
    values: Partial<Form>
    formHelpers: FormHelpersProviderReturn
  }>
}

export type LayoutItem = FieldLayoutItem | ElementLayoutItem

export type FormLayoutProps = {
  items: LayoutItem[]
  portalAttr?: string
  submitButton?: React.ReactNode
  spacingX?: GridProps['spacing']
  spacingY?: GridProps['spacing']
}

export type FormSchema = Yup.ObjectSchema<object>

export type WishOption = Omit<RadioOption, 'label'> & { label: string }

export type RadioOption = {
  value: string
  label: ReactNode
}

export enum WishesFieldType {
  RATING = 'RATING',
  TOGGLE = 'TOGGLE',
}

export type WishSettings = {
  type?: WishesFieldType
  ratingOptions?: WishOption[]
  visibleWhen?: (wishes: WishAndExpectation[]) => boolean
  updateDependantWishes?: (wish: WishAndExpectation, wishes: WishAndExpectation[]) => WishAndExpectation[]
  wishFactory?: (key: string, value: string) => WishAndExpectation
  required?: boolean
}

export type Address = {
  city?: string
  country?: string
  state?: string
  zipCode?: string
  street?: string
  number?: string
}

export type TDate = string | Date | null

export type Clinic = {
  referenceId: string
  city: string
  name: string
  officialName: string
  privacyPolicyUrl?: string
  clinicLogoUrl?: string
  houseNumber: string
  street: string
  zipCode: string
  webSiteUrl?: string
}

export type BirthdayValidationMessageObject = {
  invalidDate: string
  inPast: string
  empty: string
  maxAge: string
  adult: string
}
